import React, { useEffect, useState } from 'react';
import { syncDateRange } from '../utils/dateUtils';
import useIsMobile from '../hooks/useIsMobile';

function MaintAmcContracts({ token, selectedProperty }) {
  const { isMobile } = useIsMobile();
  const [rows, setRows] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const emptyForm = {
    contract_no: '',
    vendor_id: '',
    coverage_type: 'COMPREHENSIVE',
    start_date: '',
    end_date: '',
    contract_amount: '',
    payment_frequency: 'YEARLY',
    max_visits: '',
    terms: '',
    renewal_reminder_days: 30,
    status: 'ACTIVE'
  };
  const [form, setForm] = useState({ ...emptyForm });

  const headers = () => ({
    Authorization: 'Bearer ' + token,
    'x-chain-id': selectedProperty?.chain_id,
    'x-property-id': selectedProperty?.id
  });

  const loadRows = async () => {
    if (!selectedProperty?.id) return setRows([]);
    try {
      const res = await fetch('/api/v1/maintenance/amc-contracts', { headers: headers() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return setError(data.error || 'Failed to load AMC contracts');
      setRows(Array.isArray(data) ? data : []);
    } catch {
      setError('Network error while loading AMC contracts');
    }
  };

  const loadVendors = async () => {
    if (!selectedProperty?.id) return setVendors([]);
    try {
      const res = await fetch('/api/v1/maintenance/vendors', { headers: headers() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return setError(data.error || 'Failed to load vendors');
      setVendors(Array.isArray(data) ? data : []);
    } catch {
      setError('Network error while loading vendors');
    }
  };

  useEffect(() => {
    if (selectedProperty?.id) {
      loadRows();
      loadVendors();
    } else {
      setRows([]);
      setVendors([]);
    }
  }, [selectedProperty?.id, token]);

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...emptyForm });
    setError('');
    setShowModal(true);
  };

  const openEdit = (row) => {
    setEditingId(row.id);
    setForm({
      contract_no: row.contract_no || '',
      vendor_id: row.vendor_id || '',
      coverage_type: row.coverage_type || 'COMPREHENSIVE',
      start_date: row.start_date ? row.start_date.substring(0, 10) : '',
      end_date: row.end_date ? row.end_date.substring(0, 10) : '',
      contract_amount: row.contract_amount ?? '',
      payment_frequency: row.payment_frequency || 'YEARLY',
      max_visits: row.max_visits ?? '',
      terms: row.terms || '',
      renewal_reminder_days: row.renewal_reminder_days ?? 30,
      status: row.status || 'ACTIVE'
    });
    setError('');
    setShowModal(true);
  };

  const saveContract = async () => {
    setError('');
    if (!selectedProperty?.id) return setError('Select a property first');
    if (!form.contract_no.trim()) return setError('Contract No is required');
    if (!form.vendor_id) return setError('Vendor is required');
    if (!form.start_date) return setError('Start Date is required');
    if (!form.end_date) return setError('End Date is required');
    if (form.start_date > form.end_date) return setError('End Date must be on or after Start Date');
    const amount = Number(form.contract_amount);
    if (isNaN(amount) || amount < 0) return setError('Contract Amount must be a valid number');

    setSaving(true);
    try {
      const url = editingId
        ? `/api/v1/maintenance/amc-contracts/${editingId}`
        : '/api/v1/maintenance/amc-contracts';
      const res = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: { ...headers(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contract_no: form.contract_no.trim(),
          vendor_id: form.vendor_id,
          coverage_type: form.coverage_type,
          start_date: form.start_date,
          end_date: form.end_date,
          contract_amount: amount,
          payment_frequency: form.payment_frequency,
          max_visits: form.max_visits !== '' ? Number(form.max_visits) : null,
          terms: form.terms || null,
          renewal_reminder_days: Number(form.renewal_reminder_days) || 30,
          status: form.status
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return setError(data.error || 'Save failed');
      setShowModal(false);
      loadRows();
    } catch {
      setError('Network error while saving');
    } finally {
      setSaving(false);
    }
  };

  /* ---------- helpers ---------- */
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const in30 = new Date(today);
  in30.setDate(in30.getDate() + 30);

  const expiryStyle = (endDate) => {
    if (!endDate) return {};
    const d = new Date(endDate);
    d.setHours(0, 0, 0, 0);
    if (d < today) return { backgroundColor: '#fce4e4', color: '#b71c1c' };
    if (d <= in30) return { backgroundColor: '#fff9c4', color: '#795548' };
    return {};
  };

  const vendorName = (vendorId) => {
    const v = vendors.find((x) => String(x.id) === String(vendorId));
    return v ? v.vendor_name || v.name || vendorId : vendorId || '-';
  };

  const coverageLabel = (t) => {
    if (t === 'COMPREHENSIVE') return 'Comprehensive';
    if (t === 'NON_COMPREHENSIVE') return 'Non-Comprehensive';
    if (t === 'LABOUR_ONLY') return 'Labour Only';
    return t || '-';
  };

  const visitProgress = (used, max) => {
    const u = Number(used) || 0;
    const m = Number(max) || 0;
    if (m === 0) return <span style={{ color: '#888' }}>N/A</span>;
    const pct = Math.min((u / m) * 100, 100);
    const barColor = pct >= 90 ? '#e53935' : pct >= 70 ? '#fb8c00' : '#43a047';
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <div style={{ flex: 1, height: 8, background: '#e0e0e0', borderRadius: 4, minWidth: 60 }}>
          <div style={{ width: pct + '%', height: '100%', background: barColor, borderRadius: 4, transition: 'width 0.3s' }} />
        </div>
        <span style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{u}/{m}</span>
      </div>
    );
  };

  /* ---------- styles ---------- */
  const s = {
    page: { padding: isMobile ? 12 : 24, fontFamily: 'Inter, Segoe UI, sans-serif', maxWidth: 1200, margin: '0 auto' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
    h2: { margin: 0, fontSize: 22, fontWeight: 600 },
    btn: { padding: '8px 18px', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 14 },
    btnPrimary: { background: '#1976d2', color: '#fff' },
    btnSecondary: { background: '#e0e0e0', color: '#333' },
    btnSm: { padding: '4px 10px', fontSize: 12 },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: 14 },
    th: { textAlign: 'left', padding: '10px 12px', background: '#f5f5f5', borderBottom: '2px solid #ddd', fontWeight: 600, whiteSpace: 'nowrap' },
    td: { padding: '10px 12px', borderBottom: '1px solid #eee', verticalAlign: 'middle' },
    error: { background: '#fce4e4', color: '#b71c1c', padding: '8px 14px', borderRadius: 6, marginBottom: 12, fontSize: 14 },
    overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
    modal: { background: '#fff', borderRadius: 10, padding: isMobile ? 16 : 28, width: '95%', maxWidth: 580, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' },
    label: { display: 'block', marginBottom: 4, fontWeight: 600, fontSize: 13 },
    input: { width: '100%', padding: '8px 10px', border: '1px solid #ccc', borderRadius: 5, fontSize: 14, boxSizing: 'border-box' },
    select: { width: '100%', padding: '8px 10px', border: '1px solid #ccc', borderRadius: 5, fontSize: 14, boxSizing: 'border-box', background: '#fff' },
    textarea: { width: '100%', padding: '8px 10px', border: '1px solid #ccc', borderRadius: 5, fontSize: 14, boxSizing: 'border-box', minHeight: 70, resize: 'vertical' },
    fieldRow: { display: 'grid', gridTemplateColumns: isMobile ? 'minmax(0, 1fr)' : '1fr 1fr', gap: 14, marginBottom: 14 },
    fieldFull: { marginBottom: 14 },
    badge: (bg, fg) => ({ display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700, background: bg, color: fg })
  };

  const statusBadge = (st) => {
    if (st === 'ACTIVE') return <span style={s.badge('#e8f5e9', '#2e7d32')}>ACTIVE</span>;
    if (st === 'EXPIRED') return <span style={s.badge('#fce4e4', '#b71c1c')}>EXPIRED</span>;
    if (st === 'TERMINATED') return <span style={s.badge('#e0e0e0', '#616161')}>TERMINATED</span>;
    return <span>{st || '-'}</span>;
  };

  /* ---------- render ---------- */
  return (
    <div style={s.page}>
      <div style={s.header}>
        <h2 style={s.h2}>AMC Contracts</h2>
        <button style={{ ...s.btn, ...s.btnPrimary }} onClick={openCreate}>+ New Contract</button>
      </div>

      {error && !showModal && <div style={s.error}>{error}</div>}

      {!selectedProperty?.id ? (
        <p style={{ color: '#888' }}>Select a property from the header to view AMC contracts.</p>
      ) : rows.length === 0 ? (
        <p style={{ color: '#888' }}>No AMC contracts found. Click "+ New Contract" to create one.</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Contract No</th>
                <th style={s.th}>Vendor Name</th>
                <th style={s.th}>Coverage Type</th>
                <th style={s.th}>Start Date</th>
                <th style={s.th}>End Date</th>
                <th style={s.th}>Amount</th>
                <th style={s.th}>Visits Used/Max</th>
                <th style={s.th}>Status</th>
                <th style={s.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} style={expiryStyle(r.end_date)}>
                  <td style={s.td}>{r.contract_no || '-'}</td>
                  <td style={s.td}>{vendorName(r.vendor_id)}</td>
                  <td style={s.td}>{coverageLabel(r.coverage_type)}</td>
                  <td style={s.td}>{r.start_date ? r.start_date.substring(0, 10) : '-'}</td>
                  <td style={s.td}>{r.end_date ? r.end_date.substring(0, 10) : '-'}</td>
                  <td style={{ ...s.td, textAlign: 'right' }}>{r.contract_amount != null ? Number(r.contract_amount).toLocaleString('en-IN', { minimumFractionDigits: 2 }) : '-'}</td>
                  <td style={{ ...s.td, minWidth: 120 }}>{visitProgress(r.visits_used, r.max_visits)}</td>
                  <td style={s.td}>{statusBadge(r.status)}</td>
                  <td style={s.td}>
                    <button style={{ ...s.btn, ...s.btnSecondary, ...s.btnSm }} onClick={() => openEdit(r)}>Edit</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ---------- Create / Edit Modal ---------- */}
      {showModal && (
        <div style={s.overlay} onClick={() => setShowModal(false)}>
          <div style={s.modal} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 18px', fontSize: 18, fontWeight: 600 }}>{editingId ? 'Edit AMC Contract' : 'New AMC Contract'}</h3>

            {error && <div style={s.error}>{error}</div>}

            <div style={s.fieldRow}>
              <div>
                <label style={s.label}>Contract No *</label>
                <input style={s.input} value={form.contract_no} onChange={(e) => setForm({ ...form, contract_no: e.target.value })} />
              </div>
              <div>
                <label style={s.label}>Vendor *</label>
                <select style={s.select} value={form.vendor_id} onChange={(e) => setForm({ ...form, vendor_id: e.target.value })}>
                  <option value="">-- Select Vendor --</option>
                  {vendors.map((v) => (
                    <option key={v.id} value={v.id}>{v.vendor_name || v.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div style={s.fieldRow}>
              <div>
                <label style={s.label}>Coverage Type</label>
                <select style={s.select} value={form.coverage_type} onChange={(e) => setForm({ ...form, coverage_type: e.target.value })}>
                  <option value="COMPREHENSIVE">Comprehensive</option>
                  <option value="NON_COMPREHENSIVE">Non-Comprehensive</option>
                  <option value="LABOUR_ONLY">Labour Only</option>
                </select>
              </div>
              <div>
                <label style={s.label}>Status</label>
                <select style={s.select} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                  <option value="ACTIVE">Active</option>
                  <option value="EXPIRED">Expired</option>
                  <option value="TERMINATED">Terminated</option>
                </select>
              </div>
            </div>

            <div style={s.fieldRow}>
              <div>
                <label style={s.label}>Start Date *</label>
                <input type="date" style={s.input} value={form.start_date} onChange={(e) => setForm(syncDateRange({ ...form, start_date: e.target.value }, 'start_date', 'end_date', 'start_date'))} />
              </div>
              <div>
                <label style={s.label}>End Date *</label>
                <input type="date" style={s.input} value={form.end_date} min={form.start_date || undefined} onChange={(e) => setForm(syncDateRange({ ...form, end_date: e.target.value }, 'start_date', 'end_date', 'end_date'))} />
              </div>
            </div>

            <div style={s.fieldRow}>
              <div>
                <label style={s.label}>Contract Amount</label>
                <input type="number" style={s.input} value={form.contract_amount} onChange={(e) => setForm({ ...form, contract_amount: e.target.value })} min="0" step="0.01" />
              </div>
              <div>
                <label style={s.label}>Payment Frequency</label>
                <select style={s.select} value={form.payment_frequency} onChange={(e) => setForm({ ...form, payment_frequency: e.target.value })}>
                  <option value="MONTHLY">Monthly</option>
                  <option value="QUARTERLY">Quarterly</option>
                  <option value="YEARLY">Yearly</option>
                  <option value="ONE_TIME">One Time</option>
                </select>
              </div>
            </div>

            <div style={s.fieldRow}>
              <div>
                <label style={s.label}>Max Visits</label>
                <input type="number" style={s.input} value={form.max_visits} onChange={(e) => setForm({ ...form, max_visits: e.target.value })} min="0" />
              </div>
              <div>
                <label style={s.label}>Renewal Reminder Days</label>
                <input type="number" style={s.input} value={form.renewal_reminder_days} onChange={(e) => setForm({ ...form, renewal_reminder_days: e.target.value })} min="0" />
              </div>
            </div>

            <div style={s.fieldFull}>
              <label style={s.label}>Terms &amp; Conditions</label>
              <textarea style={s.textarea} value={form.terms} onChange={(e) => setForm({ ...form, terms: e.target.value })} />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8 }}>
              <button style={{ ...s.btn, ...s.btnSecondary }} onClick={() => setShowModal(false)} disabled={saving}>Cancel</button>
              <button style={{ ...s.btn, ...s.btnPrimary, opacity: saving ? 0.6 : 1 }} onClick={saveContract} disabled={saving}>
                {saving ? 'Saving...' : editingId ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MaintAmcContracts;
